// CHS Permission System
// Based on the JWT token structure and permission groups

import { getAuthSnapshot } from '../auth/AuthContext';
import { getLastKnownAccessToken } from '../auth/authTokenProvider';

export interface UserPermissions {
  role: string;
  permissionGroup: string;
  canEditDailyNotes: boolean;
  canEditOfficeAlerts: boolean;
  canEditInsurance: boolean;
  canEditPractices: boolean;
  canEditProviders: boolean;
  canEditAbsences: boolean;
  canEditClosures: boolean;
  canViewDisplayDate: boolean;
  canEditDisplayDate: boolean;
  isAdmin: boolean;
  canAccessFollowUpScheduling: boolean;
}

// Permission group constants
export const PERMISSION_GROUPS = {
  STANDARD_USER: 'IDM_Ping_DenimHealth_users',
  ALERT_EDITOR: 'IDM_Ping_DenimHealth_Alert',
  INSURANCE_EDITOR: 'IDM_Ping_DenimHealth_Insurance',
  ADMIN: 'IDM_Ping_DenimHealth_Admins',
  TILES_ADMIN: 'IDM_Ping_Tiles_DenimHealth',
  // TCM / Follow-Up Scheduling groups.
  // All-hospitals access: 'DenimHealth_HFUS'
  // Per-hospital access:  'DenimHealth_<hospital_id>_<hospital_name>'
  // Both share the 'DenimHealth_' prefix, so we match by prefix rather than exact value.
  TCM_ALL_HOSPITALS: 'DenimHealth_HFUS',
  TCM_HOSPITAL_PREFIX: 'DenimHealth_',
} as const;

// Role constants
export const USER_ROLES = {
  STANDARD: 'Standard_User',
  ALERT_EDITOR: 'Alert_Editor',
  INSURANCE_EDITOR: 'Insurance_Editor',
  ADMIN: 'Admin',
} as const;

const DEFAULT_PERMISSIONS: UserPermissions = {
  role: '',
  permissionGroup: '',
  canEditDailyNotes: false,
  canEditOfficeAlerts: false,
  canEditInsurance: false,
  canEditPractices: false,
  canEditProviders: false,
  canEditAbsences: false,
  canEditClosures: false,
  canViewDisplayDate: false,
  canEditDisplayDate: false,
  isAdmin: false,
  canAccessFollowUpScheduling: false,
};

/**
 * Check if authentication is fully initialized
 */
export const isAuthInitialized = (): boolean => {
  const snapshot = getAuthSnapshot();
  return Boolean(
    snapshot.isAuthenticated ||
      snapshot.userRole ||
      snapshot.permissionGroup ||
      getLastKnownAccessToken()
  );
};

/**
 * Parse permission group string and return array of permissions
 */
export const parsePermissionGroup = (
  permissionGroup: string
): string[] => {
  if (!permissionGroup) return [];
  return permissionGroup.split(',').map((p) => p.trim());
};

// Returns true for DenimHealth_HFUS (all-hospitals) or DenimHealth_<numeric_id>_<name> (per-hospital).
// Other DenimHealth_ groups are excluded because their segment after the first _ is not numeric.
const isTcmHospitalGroup = (permission: string): boolean => {
  const lower = permission.toLowerCase();
  if (lower === PERMISSION_GROUPS.TCM_ALL_HOSPITALS.toLowerCase()) return true;
  if (!lower.startsWith(PERMISSION_GROUPS.TCM_HOSPITAL_PREFIX.toLowerCase())) return false;
  const parts = permission.split('_');
  // Must have at least 3 segments: 'DenimHealth', <numeric_id>, <name>
  if (parts.length < 3 || !parts[2]) return false;
  return /^\d+$/.test(parts[1] ?? '');
};

/**
 * Case-insensitive string comparison utility
 */
const caseInsensitiveEquals = (a: string, b: string): boolean => {
  return a.toLowerCase() === b.toLowerCase();
};

type DecodedAccessTokenClaims = {
  role?: string;
  permission_group?: string;
};

const decodeJwtPayload = (token: string): DecodedAccessTokenClaims | null => {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as DecodedAccessTokenClaims;
  } catch {
    return null;
  }
};

const getRoleAndPermissionGroup = (): {
  role: string;
  permissionGroup: string;
  accessToken: string | null;
} => {
  const snapshot = getAuthSnapshot();
  const accessToken = getLastKnownAccessToken();
  const decoded = accessToken ? decodeJwtPayload(accessToken) : null;

  return {
    role: (snapshot.userRole || decoded?.role || '').trim(),
    permissionGroup: snapshot.permissionGroup || decoded?.permission_group || '',
    accessToken,
  };
};

const mergeRoleAndPermissionGroupFromJwtIfNeeded = (
  role: string,
  permissionGroup: string,
  accessToken: string | null
): { role: string; permissionGroup: string } => {
  let r = role;
  let pg = permissionGroup;
  if ((!r || !pg) && accessToken) {
    const decoded = decodeJwtPayload(accessToken);
    r = r || decoded?.role || '';
    pg = pg || decoded?.permission_group || '';
  }
  return { role: r, permissionGroup: pg };
};

export const hasAdminAccessFromStorage = (): boolean => {
  const { role: roleFromStorage, permissionGroup: permissionGroupFromStorage, accessToken } =
    getRoleAndPermissionGroup();

  const { role, permissionGroup } = mergeRoleAndPermissionGroupFromJwtIfNeeded(
    roleFromStorage,
    permissionGroupFromStorage,
    accessToken
  );

  const roleLower = role.toLowerCase();
  const permissionLower = permissionGroup.toLowerCase();
  return (
    roleLower === 'admin' ||
    permissionLower.includes('idm_ping_denimhealth_admins') ||
    permissionLower.includes('idm_ping_tiles_denimhealth')
  );
};

export const hasFollowUpSchedulingAccessFromStorage = (): boolean => {
  if (hasAdminAccessFromStorage()) return true;

  const { role: roleFromStorage, permissionGroup: permissionGroupFromStorage, accessToken } =
    getRoleAndPermissionGroup();
  const { permissionGroup } = mergeRoleAndPermissionGroupFromJwtIfNeeded(
    roleFromStorage,
    permissionGroupFromStorage,
    accessToken
  );

  return parsePermissionGroup(permissionGroup).some(isTcmHospitalGroup);
};

/**
 * Determine user permissions based on JWT token data
 */
export const getUserPermissions = (): UserPermissions => {
  const { role: roleFromSnapshot, permissionGroup: permissionGroupFromSnapshot, accessToken } =
    getRoleAndPermissionGroup();

  if (!roleFromSnapshot && !permissionGroupFromSnapshot && !accessToken) {
    return { ...DEFAULT_PERMISSIONS };
  }

  let role = roleFromSnapshot;
  let permissionGroup = permissionGroupFromSnapshot;

  ({ role, permissionGroup } = mergeRoleAndPermissionGroupFromJwtIfNeeded(
    role,
    permissionGroup,
    accessToken
  ));

  const permissions = parsePermissionGroup(permissionGroup);

  // Default permissions (Standard User)
  const userPermissions: UserPermissions = {
    role,
    permissionGroup,
    canEditDailyNotes: false,
    canEditOfficeAlerts: false,
    canEditInsurance: false,
    canEditPractices: false,
    canEditProviders: false,
    canEditAbsences: false,
    canEditClosures: false,
    canViewDisplayDate: false,
    canEditDisplayDate: false,
    isAdmin: false,
    canAccessFollowUpScheduling: false,
  };

  // Treat admin role as admin regardless of permission_group shape.
  // Some environments include role=ADMIN while still sending a non-empty permission_group.
  if (
    caseInsensitiveEquals(role, USER_ROLES.ADMIN) ||
    caseInsensitiveEquals(role, 'Admin')
  ) {
    // Admins have full access regardless of customer
    userPermissions.canEditDailyNotes = true;
    userPermissions.canEditOfficeAlerts = true;
    userPermissions.canEditInsurance = true;
    userPermissions.canEditPractices = true;
    userPermissions.canEditProviders = true;
    userPermissions.canEditAbsences = true;
    userPermissions.canEditClosures = true;
    userPermissions.canViewDisplayDate = true;
    userPermissions.canEditDisplayDate = true;
    userPermissions.isAdmin = true;
    userPermissions.canAccessFollowUpScheduling = true;
    return userPermissions;
  }

  // Check each permission group for CHS users
  permissions.forEach((permission) => {
    if (caseInsensitiveEquals(permission, PERMISSION_GROUPS.STANDARD_USER)) {
      // Standard user - no additional permissions
      return;
    }

    if (caseInsensitiveEquals(permission, PERMISSION_GROUPS.ALERT_EDITOR)) {
      // Can edit Daily Notes, Office Alerts, Absences, and Closures
      userPermissions.canEditDailyNotes = true;
      userPermissions.canEditOfficeAlerts = true;
      userPermissions.canEditAbsences = true;
      userPermissions.canEditClosures = true;
      userPermissions.canViewDisplayDate = true;
      userPermissions.canEditDisplayDate = true;
      return;
    }

    if (caseInsensitiveEquals(permission, PERMISSION_GROUPS.INSURANCE_EDITOR)) {
      // Can edit insurance grid only
      userPermissions.canEditInsurance = true;
      return;
    }

    if (
      caseInsensitiveEquals(permission, PERMISSION_GROUPS.ADMIN) ||
      caseInsensitiveEquals(permission, PERMISSION_GROUPS.TILES_ADMIN)
    ) {
      // Admin - can edit everything
      userPermissions.canEditDailyNotes = true;
      userPermissions.canEditOfficeAlerts = true;
      userPermissions.canEditInsurance = true;
      userPermissions.canEditPractices = true;
      userPermissions.canEditProviders = true;
      userPermissions.canEditAbsences = true;
      userPermissions.canEditClosures = true;
      userPermissions.canViewDisplayDate = true;
      userPermissions.canEditDisplayDate = true;
      userPermissions.isAdmin = true;
      userPermissions.canAccessFollowUpScheduling = true;
    }

    // TCM groups: all-hospitals (DenimHealth_HFUS) or per-hospital (DenimHealth_<id>_<name>)
    if (isTcmHospitalGroup(permission)) {
      userPermissions.canAccessFollowUpScheduling = true;
    }
  });

  return userPermissions;
};

/**
 * Check if user has specific permission
 */
export const hasPermission = (
  permission: keyof Omit<UserPermissions, 'role' | 'permissionGroup'>
): boolean => {
  const userPermissions = getUserPermissions();
  return userPermissions[permission];
};

/**
 * True for admins and users with all-hospitals TCM scope (DenimHealth_HFUS).
 * False for users scoped to specific hospitals only.
 */
export const isPacUser = (): boolean => {
  if (!isAuthInitialized()) return false;
  if (hasAdminAccessFromStorage()) return true;

  const { role: roleFromStorage, permissionGroup: permissionGroupFromStorage, accessToken } =
    getRoleAndPermissionGroup();
  const { permissionGroup } = mergeRoleAndPermissionGroupFromJwtIfNeeded(
    roleFromStorage,
    permissionGroupFromStorage,
    accessToken
  );

  return parsePermissionGroup(permissionGroup).some(
    (p) => p.toLowerCase() === PERMISSION_GROUPS.TCM_ALL_HOSPITALS.toLowerCase()
  );
};

/**
 * Check if user is admin
 */
export const isAdmin = (): boolean => {
  // Check if auth is initialized first
  if (!isAuthInitialized()) {
    return false;
  }

  const result = hasPermission('isAdmin');
  return result;
};

/**
 * Check if user can edit daily notes
 */
export const canEditDailyNotes = (): boolean => {
  return hasPermission('canEditDailyNotes');
};

/**
 * Check if user can edit office alerts
 */
export const canEditOfficeAlerts = (): boolean => {
  return hasPermission('canEditOfficeAlerts');
};

/**
 * Check if user can edit insurance
 */
export const canEditInsurance = (): boolean => {
  return hasPermission('canEditInsurance');
};

/**
 * Check if user can edit practices
 */
export const canEditPractices = (): boolean => {
  return hasPermission('canEditPractices');
};

/**
 * Check if user can edit providers
 */
export const canEditProviders = (): boolean => {
  return hasPermission('canEditProviders');
};

/**
 * Check if user can edit absences
 */
export const canEditAbsences = (): boolean => {
  return hasPermission('canEditAbsences');
};

/**
 * Check if user can edit closures
 */
export const canEditClosures = (): boolean => {
  return hasPermission('canEditClosures');
};

/**
 * Check if user can view display date
 */
export const canViewDisplayDate = (): boolean => {
  return hasPermission('canViewDisplayDate');
};

/**
 * Check if user can edit display date
 */
export const canEditDisplayDate = (): boolean => {
  return hasPermission('canEditDisplayDate');
};

/**
 * Check if a card should be visible to the current user based on display date
 * @param displayDate - Optional display date string (YYYY-MM-DD format)
 * @returns boolean - true if card should be visible, false if hidden
 */
export const shouldShowCard = (
  displayDate?: string | null
): boolean => {
  const userPermissions = getUserPermissions();

  // Admins and Alert Editors can see all cards regardless of display date
  if (
    userPermissions.isAdmin ||
    userPermissions.canEditDailyNotes ||
    userPermissions.canEditOfficeAlerts ||
    userPermissions.canEditAbsences ||
    userPermissions.canEditClosures
  ) {
    return true;
  }

  // If no display date is set, card is immediately visible
  if (!displayDate || displayDate.trim() === '') {
    return true;
  }

  // For standard users and insurance editors, check if display date has been reached
  // Use UTC dates to avoid timezone issues
  const today = new Date();
  const todayUTC = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  // Parse display date as UTC to avoid timezone conversion issues
  const displayDateParts = displayDate.split('-');
  if (displayDateParts.length !== 3) {
    // If date format is invalid, show the card to be safe
    return true;
  }

  const displayDateUTC = new Date(
    parseInt(displayDateParts[0]), // year
    parseInt(displayDateParts[1]) - 1, // month (0-indexed)
    parseInt(displayDateParts[2]) // day
  );

  // Card is visible if display date is today or in the past
  return displayDateUTC <= todayUTC;
};

/**
 * Get user role display name
 */
export const getUserRoleDisplayName = (): string => {
  const userPermissions = getUserPermissions();
  const role = userPermissions.role || '';
  const permissionGroup = userPermissions.permissionGroup || '';

  // Special case: Denim Health Admin
  if (
    role === USER_ROLES.ADMIN &&
    (!permissionGroup || permissionGroup === '')
  ) {
    return 'Denim Health Admin';
  }

  if (userPermissions.isAdmin) return USER_ROLES.ADMIN;
  if (userPermissions.canEditInsurance)
    return 'Standard User + Insurance Editor';
  if (userPermissions.canEditDailyNotes)
    return 'Standard User + Alert Editor';

  return 'Standard User';
};

